import {expectOneOf, HttpClient, HttpClient$} from '@raison/network-facade';
import {unSnakeByMutation} from '@raison/toolbox';
import {queryOptions} from '@tanstack/react-query';
import {type} from 'arktype';
import {atom, useAtomValue} from 'jotai';
import {CountryCodeLike} from '~/entities/countries';
import {AddressStatus} from '../model/AddressStatus';
import {Logout$} from '../model/auth/authActions';
import {SessionExpiredError} from '../model/SessionExpiredError';
import {UserStatusArktype} from '../model/UserStatus';

export const UserInfoQueryOptions$ = atom(get => {
    const {queryKey, queryFn} = queryOptions({
        queryKey: ['v1/info/full'],
        queryFn: () => fetchAndHandleUserInfo(get(HttpClient$), get(Logout$)),
    });
    return {queryKey, queryFn};
});

export function useUserInfoQueryOptions() {
    return useAtomValue(UserInfoQueryOptions$);
}

export type UserInfoResponse = Awaited<ReturnType<typeof fetchAndHandleUserInfo>>;

export async function fetchAndHandleUserInfo(http: HttpClient, logout: () => void) {
    const response = await fetchUserInfo(http);
    switch (response.status) {
        case 200:
            return response.json;
        case 401:
            logout();
            throw new SessionExpiredError();
    }
}

export async function fetchUserInfo(http: HttpClient) {
    return await http('v1/info/full').then(
        expectOneOf({
            401: null,
            200: type.object.pipe(unSnakeByMutation).to(UserInfoResponseSchema),
        })
    );
}

export type UserAlertSubject =
    | 'kyc-step-required'
    | 'address-verification-required'
    | 'card-operation-awaits-address-verification'
    | 'card-operation-awaits-card-verification';

const AlertSchema = type({
    subject: 'string',
    content: 'unknown',
    level: 'undefined | "warning" | "message"',
    date: type.number
        .pipe(n => new Date(n))
        .or('null')
        .optional(),
});

const DossierSchema = type({
    personal: {
        givenName: 'null | string',
        givenNameNational: 'null | string',
        familyName: 'null | string',
        familyNameNational: 'null | string',
        birthdate: 'null | string.date.parse',
    },
    address: {
        verifiedStatus: AddressStatus.schema,
        country: 'string',
        countryCode: CountryCodeLike,
        region: 'string',
        city: 'string',
        postalCode: 'string',
        streetAddress: 'string',
    },
});

export const ServiceProviderSchema = type({
    serviceProvider: 'string',
    status: "'requested' | 'rejected' | 'signed' | 'none'",
});
export type ServiceProviderSchema = typeof ServiceProviderSchema.infer;

export const UserInfoEntityType = type("'individual' | 'company'");
export type UserInfoEntityType = typeof UserInfoEntityType.infer;

const UserInfoResponseSchema = type({
    id: 'string | number',
    uuid: 'string',
    email: 'string',
    username: 'string',
    status: UserStatusArktype,
    verifiedEmail: 'null | string',
    intercomHash: 'string | undefined | null',
    tariff: "'Launch' | 'Growth' | 'Wealth'",
    createdAt: 'string',
    referral: {
        inviteCode: 'null | string',
    },
    dossier: DossierSchema.or('null'),
    entityType: UserInfoEntityType,
    entityLegalIds: type.null.or({
        lei: '(string | null)?',
        crn: '(string | null)?',
    }),
    serviceProviders: ServiceProviderSchema.array(),
    notifications: AlertSchema.array(),
    travelRuleEnabled: 'boolean = false',
    otpEnabled: `boolean = false`,
});
